import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  partnerLinkDelete,
  partnerLinksQuery,
  type IPartnerLinkDto,
} from '@/entities/partner';
import { useAppQuery } from '@/shared/lib/useAppQuery';
import { Paper } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';

import { CreateLinkDialog } from '../dialogs/CreateLinkDialog';
import { RenameLinkDialog } from '../dialogs/RenameLinkDialog';

export function LinksTab(): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isPending } = useAppQuery({ ...partnerLinksQuery() });

  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<IPartnerLinkDto | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const remove = useMutation({
    ...partnerLinkDelete(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['partner', 'links'] }),
  });

  const totalRegistrations = useMemo(
    () => (data ?? []).reduce((s, l) => s + l.registrationsCount, 0),
    [data]
  );

  const handleCopy = async (link: IPartnerLinkDto) => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId((id) => (id === link.id ? null : id)), 1500);
    } catch {
      // Clipboard API unavailable (insecure context, permission denied) — ignore.
    }
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: '20px' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            + {t('common:affiliatePage.linksTab.addLink')}
          </Button>
          <CustomTypography color="text.secondary">
            {t('common:affiliatePage.linksTab.totalRegistrations', { count: totalRegistrations })}
          </CustomTypography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: '20px', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 200px 120px', gap: 1, px: 3, py: 1.5, opacity: 0.6 }}>
          <CustomTypography variant="caption">{t('common:affiliatePage.linksTab.cols.name')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.linksTab.cols.url')}</CustomTypography>
          <CustomTypography variant="caption">{t('common:affiliatePage.linksTab.cols.regs')}</CustomTypography>
          <CustomTypography variant="caption" />
        </Box>
        <Divider />

        {isPending && !data ? (
          <Loader containerSize="medium" />
        ) : (data ?? []).length === 0 ? (
          <NotFoundData
            title={t('common:affiliatePage.linksNotFoundTitle')}
            caption={t('common:affiliatePage.linksNotFoundCaption')}
          />
        ) : (
          (data ?? []).map((link) => (
            <Box
              key={link.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 2fr 200px 120px',
                gap: 1,
                px: 3,
                py: 1.5,
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'custom.black.10',
              }}
            >
              <CustomTypography>{link.name}</CustomTypography>
              <CustomTypography sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {link.url}
              </CustomTypography>
              <CustomTypography>{link.registrationsCount}</CustomTypography>
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title={t('common:affiliatePage.linksTab.actions.rename') ?? ''} arrow>
                  <IconButton size="small" onClick={() => setRenameTarget(link)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common:affiliatePage.linksTab.actions.copy') ?? ''} arrow>
                  <IconButton size="small" onClick={() => handleCopy(link)}>
                    {copiedId === link.id ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common:affiliatePage.actions.delete') ?? ''} arrow>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={remove.isPending && remove.variables === link.id}
                    onClick={() => remove.mutate(link.id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))
        )}
      </Paper>

      <CreateLinkDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <RenameLinkDialog
        open={!!renameTarget}
        linkId={renameTarget?.id ?? null}
        initialName={renameTarget?.name ?? ''}
        onClose={() => setRenameTarget(null)}
      />
    </Stack>
  );
}
